import { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import EarningsOverview from './components/EarningsOverview';
import BizznetNotifications from './components/BizznetNotifications';
import MonthlyEarnings from './components/MonthlyEarnings';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { affiliateService } from '../../services/affiliateService';
import { notificationService } from '../../services/notificationService';

const AffiliateDashboard = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [earnings, setEarnings] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/user-login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      loadAffiliateData();
    }
  }, [user]);

  const loadAffiliateData = async () => {
    try {
      setLoading(true);
      const [earningsData, notificationsData] = await Promise.all([
        affiliateService?.getEarnings(user?.id),
        notificationService?.getNotifications(user?.id)
      ]);
      
      setEarnings(earningsData);
      setNotifications(notificationsData?.filter(n => 
        ['withdrawal_rejected', 'withdrawal_approved', 'withdrawal_processing', 'recommendation']?.includes(n?.notificationType)
      ));
    } catch (error) {
      console.error('Error loading affiliate data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  const mockUser = {
    type: 'user',
    membershipTier: userProfile?.membership_tier || 'Elite',
    notifications: {
      dashboard: 3,
      affiliate: notifications?.filter(n => !n?.isRead)?.length || 0
    }
  };

  const earningsData = {
    totalEarnings: earnings?.totalEarnings || 0,
    totalWithdrawn: earnings?.totalWithdrawn || 0,
    totalPoints: earnings?.totalPoints || 0,
    withdrawalEligible: earnings?.withdrawalEligible || false,
    withdrawalMinimum: 200000
  };

  const monthlyEarningsData = {
    currentMonth: earnings?.totalEarnings || 0,
    previousMonth: 0,
    projection: earnings?.totalEarnings ? earnings?.totalEarnings * 1.15 : 0,
    level1Earnings: earnings?.level1Earnings || 0,
    level2Earnings: earnings?.level2Earnings || 0
  };

  const mockNotifications = [
    {
      id: "NOT001",
      type: "withdrawal_rejected",
      title: "Retiro Rechazado - WTH003",
      message: "Tu solicitud de retiro por $250.000 COP ha sido rechazada.",
      observation: "El número de cuenta Nequi proporcionado no coincide con el nombre del titular registrado en tu perfil.",
      recommendation: "Por favor, verifica que el número de cuenta Nequi esté a tu nombre o actualiza tu información de perfil. Luego, realiza una nueva solicitud de retiro.",
      withdrawalId: "WTH003",
      date: "26/12/2025"
    },
    {
      id: "NOT002",
      type: "withdrawal_approved",
      title: "Retiro Aprobado - WTH002",
      message: "Tu retiro de $150.000 COP ha sido aprobado y procesado exitosamente.",
      observation: "El pago fue transferido a tu cuenta Bancolombia terminada en ****5678.",
      recommendation: "El dinero debería reflejarse en tu cuenta en las próximas 24-48 horas hábiles.",
      withdrawalId: "WTH002",
      date: "25/12/2025"
    },
    {
      id: "NOT003",
      type: "withdrawal_processing",
      title: "Retiro en Proceso - WTH001",
      message: "Tu solicitud de retiro por $200.000 COP está siendo procesada.",
      observation: "Estamos verificando la información bancaria proporcionada.",
      recommendation: "El proceso puede tomar de 2 a 3 días hábiles. Te notificaremos cuando se complete.",
      withdrawalId: "WTH001",
      date: "24/12/2025"
    },
    {
      id: "NOT004",
      type: "recommendation",
      title: "Actualiza tu Información de Pago",
      message: "Para evitar rechazos en futuros retiros, te recomendamos verificar tu información.",
      recommendation: "Asegúrate de que todos tus datos bancarios estén actualizados y coincidan con tu documento de identidad registrado.",
      date: "23/12/2025"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header user={mockUser} />
      <main className="main-content">
        <div className="max-w-7xl mx-auto py-8 space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-foreground mb-2">Bizznet Rewards</h1>
              <p className="text-muted-foreground">Gestiona tus referidos y maximiza tus ganancias</p>
            </div>
          </div>

          <EarningsOverview
            totalEarnings={earningsData?.totalEarnings}
            totalWithdrawn={earningsData?.totalWithdrawn}
            totalPoints={earningsData?.totalPoints}
            withdrawalEligible={earningsData?.withdrawalEligible}
            withdrawalMinimum={earningsData?.withdrawalMinimum}
          />

          <BizznetNotifications notifications={mockNotifications} />

          <MonthlyEarnings
            currentMonth={monthlyEarningsData?.currentMonth}
            previousMonth={monthlyEarningsData?.previousMonth}
            projection={monthlyEarningsData?.projection}
            level1Earnings={monthlyEarningsData?.level1Earnings}
            level2Earnings={monthlyEarningsData?.level2Earnings}
          />

          {/* Manage Affiliates Button */}
          <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/30 rounded-lg p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="Settings" size={24} color="white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Herramientas de Gestión</h3>
                  <p className="text-sm text-muted-foreground mb-1">
                    Accede a herramientas avanzadas para gestionar tus afiliados:
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1 mt-2">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      Calculadora de Comisiones
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      Gestión de Enlaces de Referido
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      Compartir en Redes Sociales
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      Gestión de Retiros
                    </li>
                  </ul>
                </div>
              </div>
              <Button
                variant="default"
                size="lg"
                onClick={() => navigate('/affiliate-management')}
                iconName="ArrowRight"
                iconPosition="right"
                className="whitespace-nowrap"
              >
                Gestionar Afiliados
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AffiliateDashboard;